import { Button, Text, Section } from "@/components";
import { useApp } from "@/hooks";
import './Banner.scss';

const BANNER_DESCRIPTIONS = [
  {
    id: 1,
    title: "For Institutional Investors",
    texts: [
      "AI built for VC, PE, RE, and Family Offices where pace, precision, & privacy matter",
    ]
  },
  {
    id: 2,
    title: "Due Diligence Efficiency",
    texts: [
      "Accelerate your team’s diligence process without sacrificing depth"
    ]
  },
  {
    id: 3,
    title: "Maximum Privacy",
    texts: [
      "AI that never touches a public LLM and runs on your private cloud or local machine"
    ]
  }
];

export const Banner = () => {
  const { doGetStarted } = useApp();

  return (
    <>
      <Section
        className="bg-black flex flex-col justify-center items-center mt-[16px] md:mt-1 py-5 md:pb-0 gap-0 md:gap-6 overflow-hidden"
      >
        <div className="flex flex-col items-center gap-2">
          <Text.Heading size="md" className="max-w-[1200px] text-center text-secondary text-[#47E0E7] text-[40px] md:text-[88px] md:leading-[66px]">
            Deal Qualification
          </Text.Heading>

          <Text.Heading
            size="md"
            className="delivered-today-text text-[28px] md:text-[74px] md:leading-auto"
          >
            In Days, Not Weeks
          </Text.Heading>
        </div>

        {/* mobile mac image */}
        <div className="mx-5 mt-4 mb-0 block md:hidden">
          <div className="relative">
            {/* vertical lines on left/right of the mac */}
            <img
              className="w-auto h-auto absolute left-[-10px] top-[20px]"
              alt="New diligence flags"
              src="/images/home/mobile-left-vertical-lines.svg"
              loading="lazy" // Added lazy loading
            />

            <img
              className="w-auto h-auto absolute right-[-35px] top-[-25px]"
              alt="New diligence flags"
              src="/images/home/mobile-right-vertical-lines.svg"
              loading="lazy" // Added lazy loading
            />

            <img
              className="relative w-full h-auto z-10"
              alt="New diligence flags"
              src="/images/home/new--diligence-flags-web.png"
              loading="lazy" // Added lazy loading
            />
            <img
              className="w-full h-[60px] mt-[-30px]"
              alt="mac background"
              src="/images/home/mobile-mac-background.png"
              loading="lazy" // Added lazy loading
            />
            <img
              className="w-full h-[60px] mt-[-80px]"
              alt="mac background"
              src="/images/home/mobile-grid.png"
              loading="lazy" // Added lazy loading
            />
          </div>
        </div>

        {/* Desktop version */}
        <div className="hidden max-w-[1097px] mt-5 lg:mt-[60px] lg:grid grid-cols-1 lg:grid-cols-3 gap-[45px] lg:gap-[17px] place-items-center z-10 text-center">
          {BANNER_DESCRIPTIONS.map((feature) => (
            <div key={feature.id} className="banner-description-card w-[328px] h-[130px] z-10">
              <div className="relative flex w-full h-full banner-description-card-content">
                <img
                  src="/images/home/banner-circle-check.svg"
                  alt="Check icon"
                  className="absolute top-[-24px] left-[calc(50%-24px)] w-12 h-12"
                  loading="lazy" // Added lazy loading
                />
                <div className="relative m-auto">
                  <div className="text-center max-w-[288px]">
                    <Text.Description size="sm" className="!font-[500] md:!font-[500] text-white text-[18px] !leading-[36px]">
                      {feature.title}
                    </Text.Description>

                    {feature.texts.map((text, index) => (
                      <Text.Description
                        key={`security-${feature.id}-${index}`}
                        size="xs"
                        className="font-light !text-[#A9AAB0] !text-[14px]"
                      >
                        {text}
                      </Text.Description>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile version */}
        <div className="grid grid-cols-1 gap-[12px] lg:hidden w-full max-w-[390px] z-10 text-center">
          {BANNER_DESCRIPTIONS.map((feature) => (
            <div key={feature.id} className="banner-description-card z-10">
              <div className="relative w-full h-full flex items-center px-5 py-[7px] banner-description-card-content">
                <img
                  src="/images/home/banner-circle-check.svg"
                  alt="Check icon"
                  className="relative w-8 h-8 mr-3"
                  loading="lazy" // Added lazy loading
                />
                <div className="relative my-auto">
                  <div className="text-center max-w-[302px]">
                    <Text.Description size="sm" className="!font-[500] text-white text-left !text-[18px] !leading-[36px]">
                      {feature.title}
                    </Text.Description>

                    {feature.texts.map((text, index) => (
                      <Text.Description
                        key={`security-${feature.id}-${index}`}
                        size="xs"
                        className="font-light text-left !text-[#A9AAB0] !text-[14px]"
                      >
                        {text}
                      </Text.Description>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="block w-full max-w-[390px] mt-2">
            <div className="flex flex-col items-center md:justify-center mb-0 md:mb-2 gap-[10px] w-full">
              <Button onClick={doGetStarted} className="z-[1] flex items-center justify-center w-full h-[60px] text-[18px] leading-[27px] rounded-[8px] border-none px-8">
                Get Started - 14 Day Free Trial
              </Button>

              <Text.Description size="sm" className="z-[1] !text-[16px] !text-[#C3C3C3] !leading-[26px]">No credit card required</Text.Description>
            </div>
          </div>
        </div>
      </Section>

      <div className="hidden md:block w-full mx-auto md:max-w-[90%] xl:max-w-[1120px]">
        {/* desktop mac image */}
        <div className="relative">
          {/* vertical lines on left/right of the mac */}
          <img
            className="w-auto h-auto absolute left-[-120px] top-[-235px]"
            alt="New diligence flags"
            src="/images/home/left-vertical-lines.svg"
            loading="lazy" // Added lazy loading
          />

          <img
            className="w-auto h-auto absolute right-[-120px] top-[-75px]"
            alt="New diligence flags"
            src="/images/home/right-vertical-lines.svg"
            loading="lazy" // Added lazy loading
          />

          <img
            className="w-full h-auto object-cover"
            alt="New diligence flags"
            src="/images/home/new--diligence-flags-web.png" // use less quality image for performance
            loading="lazy" // Added lazy loading
          />

          {/* <div className="relative">
            <img
              className="absolute w-full h-[190px] mt-[-60px]"
              alt="mac background"
              src="/images/home/mac-background.png"
              loading="lazy" // Added lazy loading
            />
            <img
              className="absolute w-full h-[133px] mt-[-28px]"
              alt="mac background"
              src="/images/home/grid.png"
              loading="lazy" // Added lazy loading
            />
          </div> */}
        </div>

        <div className="hidden lg:block w-full md:mt-2">
          <div className="flex flex-col items-center md:justify-center mb-0 md:mb-2 gap-[12px]">
            <Button onClick={doGetStarted} className="z-[1] flex items-center justify-center w-[408px] h-[60px] text-[18px] leading-[27px] rounded-[8px] border-none">
              Get Started - 14 Day Free Trial
            </Button>

            <Text.Description size="sm" className="z-[1] !text-[#C3C3C3] !leading-[26px]">No credit card required</Text.Description>
          </div>
        </div>
      </div>
    </>
  )
};
